import {
  MATCH_FILTER_CONDITION_ALL,
  MATCH_FILTER_CONDITION_AT_LEAST_ONE,
  SELECT_MAX_ITEMS
} from 'Constants/index'
import debounce from 'lodash.debounce'
import PropTypes from 'prop-types'
import { useRef } from 'react'
import { useIntl } from 'react-intl'
import {
  useRecoilCallback,
  useRecoilState,
  useRecoilValue,
  useSetRecoilState
} from 'recoil'

import {
  Chip,
  ChipIconInfo,
  ChipIconTimes,
  ChipIconWrapper,
  Flex,
  FlexItem,
  Grid,
  GridCell,
  Select,
  SELECT_VARIANT_WITH_CHECKBOX,
  Switcher,
  Text,
  useAsyncSelect
} from 'mailup-web-components'

import { fetchFiltersCallback } from 'Pages/Campaign/recoil/actions/asyncSelectActions'
import { onSelectErrorCallback } from 'Pages/Campaign/recoil/callbacks/callbacks'
import { campaignState } from 'Pages/Campaign/recoil/state/campaign'
import { modalFilterDetailsState } from 'Pages/Campaign/recoil/state/modals'
import { segmentationFiltersState } from 'Pages/Campaign/recoil/state/page'

const ApplyFilters = () => {
  const intl = useIntl()

  // async select
  const { optionGroups } = useRecoilValue(segmentationFiltersState)
  const [
    {
      EmailOptions: { ApplyFilter, ApplyFilterCondition }
    },
    setEmailOptions
  ] = useRecoilState(campaignState)
  const setModalFilterDetails = useSetRecoilState(modalFilterDetailsState)
  const onError = useRecoilCallback(onSelectErrorCallback, [])
  const fetchFilters = useRecoilCallback(fetchFiltersCallback, [])

  const getInitialItems = () => fetchFilters()
  const getItemsByTerm = (searchTerm) => fetchFilters(searchTerm)
  const getItemById = () => {}

  const maxItems = SELECT_MAX_ITEMS

  const hints = {
    loading: intl.formatMessage({ id: 'Load.MailupCommon.InputStatusLoading' }),
    moreResults: intl.formatMessage({
      id: 'Load.Filters.ActivityMessagesMoreResults'
    }),
    noResults: intl.formatMessage({
      id: 'Load.MailupCommon.InputStatusNoMatches'
    })
  }

  const { onSearch, onChange, ...props } = useAsyncSelect({
    initialValue: ApplyFilter || [],
    getInitialItems,
    getItemsByTerm,
    getItemById,
    onError,
    hints,
    maxItems
  })

  const handleChange = (v) => {
    onChange(v)
    setEmailOptions((prev) => ({
      ...prev,
      EmailOptions: {
        ...prev.EmailOptions,
        ApplyFilter: v
      },
      isDirty: true
    }))
  }

  const { current: debouncedOnSearch } = useRef(debounce(onSearch, 350))

  const handleSearch = (s) => {
    debouncedOnSearch.cancel()
    debouncedOnSearch(s)
    return s
  }
  // --

  const handleSwitcher = (value) => {
    setEmailOptions((prev) => ({
      ...prev,
      EmailOptions: {
        ...prev.EmailOptions,
        ApplyFilterCondition: value === MATCH_FILTER_CONDITION_AT_LEAST_ONE
      },
      isDirty: true
    }))
  }

  const SelectedItemComponent = ({
    label,
    value,
    type,
    name,
    description,
    onClick
  }) => {
    return (
      <Chip label={label} value={value}>
        <ChipIconWrapper
          onClick={() =>
            setModalFilterDetails({
              show: true,
              type,
              name,
              description,
              id: value
            })
          }
          data-mu-test='campaign-wizard__step-3__apply-filter-info'
        >
          <ChipIconInfo />
        </ChipIconWrapper>
        <ChipIconWrapper
          onClick={(e) => onClick(value, e)}
          data-mu-test='campaign-wizard__step-3__apply-filter-remove'
        >
          <ChipIconTimes />
        </ChipIconWrapper>
      </Chip>
    )
  }
  SelectedItemComponent.propTypes = {
    label: PropTypes.string,
    value: PropTypes.number,
    type: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    onClick: PropTypes.func
  }

  return (
    <>
      <Grid cols={12}>
        <GridCell span={4}>
          <Flex
            justifyContent='flex-start'
            alignItems='baseline'
            margin={
              (ApplyFilter || []).length <= 1 ? '8px 0 0 0' : '4px 8px 0 0'
            }
          >
            <Text>
              {intl.formatMessage({
                id: 'Load.Task.ConditionsGroupAndFilterLabelApply'
              })}
            </Text>
            <FlexItem>
              {(ApplyFilter || []).length > 1 && (
                <Switcher
                  value={
                    ApplyFilterCondition === true
                      ? MATCH_FILTER_CONDITION_AT_LEAST_ONE
                      : MATCH_FILTER_CONDITION_ALL
                  }
                  onChange={handleSwitcher}
                  options={[
                    {
                      label: intl.formatMessage({
                        id: 'Load.SearchRecipients.filterAll'
                      }),
                      value: MATCH_FILTER_CONDITION_ALL,
                      htmlAttrs: {
                        'data-tip': intl.formatMessage({
                          id: 'Load.SearchRecipients.filterAllTooltip'
                        })
                      }
                    },
                    {
                      label: intl.formatMessage({
                        id: 'Load.SearchRecipients.filterOne'
                      }),
                      value: MATCH_FILTER_CONDITION_AT_LEAST_ONE,
                      htmlAttrs: {
                        'data-tip': intl.formatMessage({
                          id: 'Load.SearchRecipients.filterOneTooltip'
                        })
                      }
                    }
                  ]}
                />
              )}
            </FlexItem>
          </Flex>
        </GridCell>
        <GridCell span={8}>
          <Flex display='block' justifyContent='flex-start' width='100%'>
            <Select
              data-mu-test='campaign__step-3__select-apply-filters'
              searchable
              multiple
              placeholder={intl.formatMessage({
                id: 'Load.Task.ConditionsGroupAndFilterFilterPlaceholder'
              })}
              variant={SELECT_VARIANT_WITH_CHECKBOX}
              onChange={handleChange}
              onSearch={handleSearch}
              value={ApplyFilter || []}
              optionGroups={optionGroups}
              SelectedItemComponent={SelectedItemComponent}
              {...props}
            />
          </Flex>
        </GridCell>
      </Grid>
    </>
  )
}

export default ApplyFilters
